import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function SupportInbox() {
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) || null,
    [threads, selectedThreadId]
  );

  useEffect(() => {
    loadThreads();
  }, []);

  useEffect(() => {
    if (selectedThreadId) {
      loadMessages(selectedThreadId);
    }
  }, [selectedThreadId]);

  const getAuthConfig = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const loadThreads = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/support/admin/threads', getAuthConfig());
      setThreads(response.data);
      if (!selectedThreadId && response.data.length > 0) {
        setSelectedThreadId(response.data[0].id);
      }
    } catch (error) {
      console.error('Ошибка загрузки обращений:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (threadId) => {
    try {
      const response = await axios.get(
        `/api/v1/support/admin/threads/${threadId}/messages`,
        getAuthConfig()
      );
      setMessages(response.data);
      await loadThreads();
    } catch (error) {
      console.error('Ошибка загрузки сообщений поддержки:', error);
    }
  };

  const sendReply = async () => {
    const content = reply.trim();
    if (!content || !selectedThreadId) return;

    setSending(true);
    try {
      await axios.post(
        `/api/v1/support/admin/threads/${selectedThreadId}/messages`,
        { content },
        getAuthConfig()
      );
      setReply('');
      await loadMessages(selectedThreadId);
    } catch (error) {
      console.error('Ошибка ответа в поддержку:', error);
      alert('Не удалось отправить ответ: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  const closeThread = async () => {
    if (!selectedThreadId) return;
    try {
      await axios.post(
        `/api/v1/support/admin/threads/${selectedThreadId}/close`,
        {},
        getAuthConfig()
      );
      await loadThreads();
    } catch (error) {
      alert('Ошибка закрытия обращения: ' + (error.response?.data?.detail || error.message));
    }
  };

  const reopenThread = async () => {
    if (!selectedThreadId) return;
    try {
      await axios.post(
        `/api/v1/support/admin/threads/${selectedThreadId}/reopen`,
        {},
        getAuthConfig()
      );
      await loadThreads();
    } catch (error) {
      alert('Ошибка повторного открытия: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="admin-support-loading">Загрузка обращений поддержки...</div>;
  }

  return (
    <div className="admin-support-section">
      <div className="admin-support-header">
        <h3>Техподдержка</h3>
        <button onClick={loadThreads} className="btn-small btn-info">
          Обновить
        </button>
      </div>

      <div className="admin-support-layout">
        <div className="admin-support-sidebar">
          {threads.length === 0 ? (
            <div className="admin-support-empty">Обращений пока нет</div>
          ) : (
            threads.map((thread) => (
              <button
                key={thread.id}
                className={`support-thread-item ${thread.id === selectedThreadId ? 'active' : ''}`}
                onClick={() => setSelectedThreadId(thread.id)}
              >
                <div className="support-thread-top">
                  <strong>{thread.owner_email || 'Клиент'}</strong>
                  <span className={`support-thread-status ${thread.status}`}>
                    {thread.status === 'open' ? 'Открыто' : 'Закрыто'}
                  </span>
                </div>
                <div className="support-thread-meta">
                  {thread.tenant_name || 'Без компании'}
                </div>
                <div className="support-thread-preview">
                  {thread.last_message_preview || 'Нет сообщений'}
                </div>
                <div className="support-thread-bottom">
                  <span>{formatDate(thread.last_message_at || thread.updated_at)}</span>
                  {thread.unread_count > 0 && (
                    <span className="support-thread-badge">{thread.unread_count}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="admin-support-chat">
          {selectedThread ? (
            <>
              <div className="admin-support-chat-header">
                <div>
                  <strong>{selectedThread.owner_email || 'Клиент'}</strong>
                  <div className="admin-support-chat-subtitle">
                    {selectedThread.tenant_name || 'Без компании'}
                  </div>
                </div>
                <div className="admin-support-chat-actions">
                  {selectedThread.status === 'open' ? (
                    <button className="btn-small btn-warning" onClick={closeThread}>
                      Закрыть
                    </button>
                  ) : (
                    <button className="btn-small btn-success" onClick={reopenThread}>
                      Открыть снова
                    </button>
                  )}
                </div>
              </div>

              <div className="admin-support-messages">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`admin-support-message ${msg.sender_type === 'admin' ? 'from-admin' : 'from-user'}`}
                  >
                    <div className="admin-support-message-sender">
                      {msg.sender_type === 'admin' ? (msg.sender_name || 'Техподдержка') : (msg.sender_name || 'Клиент')}
                    </div>
                    <div className="admin-support-message-content">{msg.content}</div>
                    <div className="admin-support-message-time">{formatDate(msg.created_at)}</div>
                  </div>
                ))}
              </div>

              <div className="admin-support-reply">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Ответить клиенту..."
                  rows={3}
                  disabled={sending}
                />
                <button onClick={sendReply} disabled={sending || !reply.trim()}>
                  {sending ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </>
          ) : (
            <div className="admin-support-empty-chat">Выберите обращение слева</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupportInbox;